import { useEffect, useRef, useState } from "react";
import { clienteApi, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Upload, Trash2, ChevronDown, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";

/**
 * Sheet lateral para o cliente solicitar um orçamento.
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open: boolean) => void
 *  - onCreated: () => void  // callback após criar solicitação
 */
export default function SolicitarOrcamento({ open, onOpenChange, onCreated }) {
  const [enderecos, setEnderecos] = useState({ principal: "", adicionais: [] });
  const [selEnderecoId, setSelEnderecoId] = useState("principal"); // "principal" | id | "novo"
  const [novoApelido, setNovoApelido] = useState("");
  const [novoEndereco, setNovoEndereco] = useState("");
  const [itens, setItens] = useState([{ descricao: "", foto: null, uploading: false }]);
  const [observacao, setObservacao] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset ao abrir
    setItens([{ descricao: "", foto: null, uploading: false }]);
    setObservacao("");
    setNovoApelido("");
    setNovoEndereco("");
    setSelEnderecoId("principal");
    // Carregar endereços
    clienteApi.get("/portal/enderecos")
      .then(({ data }) => {
        setEnderecos(data);
        if (!data.principal && (!data.adicionais || data.adicionais.length === 0)) {
          setSelEnderecoId("novo");
        }
      })
      .catch(() => toast.error("Erro ao carregar endereços"));
  }, [open]);

  const addItem = () => setItens((arr) => [...arr, { descricao: "", foto: null, uploading: false }]);
  const removeItem = (idx) => setItens((arr) => arr.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) =>
    setItens((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const uploadFotoItem = async (idx, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Foto muito grande (máx. 10MB)");
      return;
    }
    updateItem(idx, { uploading: true });
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await clienteApi.post("/portal/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Guardar o path storage (ex: bbh-orcamentos/uploads/xxx.jpg) para o backend montar depois
      updateItem(idx, { foto: data.path, fotoPreview: data.url, uploading: false });
    } catch (e) {
      updateItem(idx, { uploading: false });
      toast.error(e?.response?.data?.detail || "Falha ao enviar foto");
    }
  };

  const enviar = async () => {
    // Validações
    const itensValidos = itens.filter((it) => (it.descricao || "").trim().length > 0);
    if (itensValidos.length === 0) {
      return toast.error("Descreva pelo menos um serviço.");
    }
    if (selEnderecoId === "novo") {
      if (!novoApelido.trim() || !novoEndereco.trim()) {
        return toast.error("Preencha o apelido e o endereço.");
      }
    } else if (selEnderecoId === "principal" && !enderecos.principal) {
      return toast.error("Você não tem um endereço principal. Cadastre um novo endereço.");
    }

    setEnviando(true);
    try {
      const payload = {
        itens: itensValidos.map((it) => ({
          descricao: it.descricao.trim(),
          foto: it.foto || null,
        })),
        observacao: observacao.trim() || null,
      };
      if (selEnderecoId === "novo") {
        payload.novo_endereco = { apelido: novoApelido.trim(), endereco: novoEndereco.trim() };
      } else {
        payload.endereco_id = selEnderecoId;
      }
      await clienteApi.post("/portal/solicitacoes", payload);
      toast.success("Solicitação enviada! Em breve entraremos em contato.");
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar solicitação.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg p-0 overflow-y-auto"
        data-testid="solicitar-orcamento-sheet"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <SheetTitle className="sr-only">Solicitar orçamento</SheetTitle>
        <SheetDescription className="sr-only">
          Envie os detalhes dos serviços que você precisa para receber um orçamento.
        </SheetDescription>
        <div className="p-5 sm:p-6 space-y-6">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
              Portal do cliente
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Solicitar orçamento</h2>
            <p className="text-sm text-zinc-600 mt-1">
              Descreva os serviços desejados. Você pode anexar uma foto por serviço.
            </p>
          </div>

          {/* Endereço */}
          <section>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              Endereço do serviço
            </label>
            <div className="mt-2 space-y-2">
              {enderecos.principal && (
                <EnderecoOption
                  ativo={selEnderecoId === "principal"}
                  onClick={() => setSelEnderecoId("principal")}
                  titulo="Meu endereço principal"
                  descricao={enderecos.principal}
                  testid="endereco-opt-principal"
                />
              )}
              {(enderecos.adicionais || []).map((e) => (
                <EnderecoOption
                  key={e.id}
                  ativo={selEnderecoId === e.id}
                  onClick={() => setSelEnderecoId(e.id)}
                  titulo={e.apelido}
                  descricao={e.endereco}
                  testid={`endereco-opt-${e.id}`}
                />
              ))}
              <EnderecoOption
                ativo={selEnderecoId === "novo"}
                onClick={() => setSelEnderecoId("novo")}
                titulo="Outro endereço"
                descricao="Adicione um novo endereço ao seu cadastro"
                testid="endereco-opt-novo"
              />
            </div>

            {selEnderecoId === "novo" && (
              <div className="mt-3 bbh-card p-3 space-y-2 bg-zinc-50">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                    Apelido do endereço
                  </label>
                  <input
                    type="text"
                    value={novoApelido}
                    onChange={(e) => setNovoApelido(e.target.value)}
                    placeholder="Ex: Casa da minha mãe"
                    data-testid="novo-endereco-apelido"
                    className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                    Endereço completo
                  </label>
                  <textarea
                    rows={2}
                    value={novoEndereco}
                    onChange={(e) => setNovoEndereco(e.target.value)}
                    placeholder="Rua, número, bairro, cidade..."
                    data-testid="novo-endereco-texto"
                    className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm"
                  />
                </div>
                <div className="text-xs text-zinc-500">
                  Este endereço será salvo no seu cadastro para uso futuro.
                </div>
              </div>
            )}
          </section>

          {/* Itens */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                Serviços desejados
              </label>
              <span className="text-xs text-zinc-500">{itens.length} {itens.length === 1 ? "serviço" : "serviços"}</span>
            </div>
            <div className="space-y-3">
              {itens.map((it, idx) => (
                <ItemForm
                  key={idx}
                  index={idx}
                  item={it}
                  onChange={(patch) => updateItem(idx, patch)}
                  onRemove={itens.length > 1 ? () => removeItem(idx) : null}
                  onUpload={(f) => uploadFotoItem(idx, f)}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={addItem}
              data-testid="add-item-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-dashed border-zinc-300 text-zinc-700 text-sm font-semibold hover:bg-zinc-50"
            >
              <Plus className="w-4 h-4" /> Adicionar outro serviço
            </button>
          </section>

          {/* Observação */}
          <section>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              Observação (opcional)
            </label>
            <textarea
              rows={3}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Alguma informação adicional para a BBH?"
              data-testid="solicitacao-obs"
              className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm"
            />
          </section>

          <div className="sticky bottom-0 -mx-5 sm:-mx-6 px-5 sm:px-6 py-3 bg-white border-t border-zinc-200 flex gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 px-4 py-2.5 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-700"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={enviar}
              disabled={enviando}
              data-testid="enviar-solicitacao-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold disabled:opacity-60"
            >
              {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
              {enviando ? "Enviando…" : "Enviar solicitação"}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EnderecoOption({ ativo, onClick, titulo, descricao, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-3 py-2.5 rounded-md border transition ${
        ativo
          ? "border-[#14243D] ring-2 ring-[#14243D]/10 bg-[#14243D]/5"
          : "border-zinc-200 hover:border-zinc-400"
      }`}
    >
      <div className="text-sm font-semibold text-zinc-900">{titulo}</div>
      <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{descricao}</div>
    </button>
  );
}

function ItemForm({ index, item, onChange, onRemove, onUpload }) {
  const fileRef = useRef(null);
  return (
    <div className="bbh-card p-3 space-y-2" data-testid={`item-form-${index}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mt-1">
          Serviço {index + 1}
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            data-testid={`remove-item-${index}`}
            className="p-1 rounded text-zinc-400 hover:text-rose-600 hover:bg-rose-50"
            aria-label="Remover serviço"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <textarea
        rows={2}
        value={item.descricao}
        onChange={(e) => onChange({ descricao: e.target.value })}
        placeholder="Descreva o serviço que precisa..."
        data-testid={`item-descricao-${index}`}
        className="w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm"
      />
      <div className="flex items-center gap-3">
        {item.fotoPreview ? (
          <div className="relative">
            <img
              src={item.fotoPreview}
              alt=""
              className="w-16 h-16 object-cover rounded-md border border-zinc-200"
            />
            <button
              type="button"
              onClick={() => onChange({ foto: null, fotoPreview: null })}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-zinc-200 text-zinc-500 hover:text-rose-600 flex items-center justify-center shadow"
              aria-label="Remover foto"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={item.uploading}
            data-testid={`item-foto-btn-${index}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
          >
            {item.uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {item.uploading ? "Enviando…" : "Adicionar foto"}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
        <div className="text-xs text-zinc-400">Opcional · Uma foto por serviço</div>
      </div>
    </div>
  );
}
